/**
 * Format screening for theme values that are typed by hand.
 *
 * WHY THIS EXISTS
 * Every theme token lands in a CSS custom property via `setProperty()`, and
 * PMM stores them as opaque strings -- it explicitly does not validate or
 * coerce a theme value. So the editor is the only gate there is.
 *
 * An unscreened value fails in the worst available way. `setProperty()` with
 * an invalid value silently DROPS the declaration, so the control appears to
 * do nothing; but the stored string is still non-empty, so `hasTheme()` counts
 * the preference as themed, which opts it out of fill-only fan-out. The result
 * is a preference that paints nothing, cannot be fixed by fan-out, and gives
 * no indication of either.
 *
 * `ColorInput` has guarded this since round 2 and the argument was never
 * carried to the other input types -- so a CSS shorthand and the length
 * escape hatch both wrote unscreened strings.
 *
 * WHY DELEGATE TO THE BROWSER
 * `CSS.supports(property, value)` runs the real parser, which means it accepts
 * everything a client may legitimately want -- `clamp()`, `calc()`, `min()`,
 * `var()`, `oklch()` -- without us maintaining a grammar we would get wrong.
 * That matters here specifically: the length control's whole purpose is to
 * stop destroying fluid type like `clamp(1.5rem, .25rem + 2.67vw, 2.25rem)`,
 * and a hand-rolled regex would reject exactly that.
 */

/**
 * Percentages need their own check.
 *
 * They are consumed inside `color-mix(..., N%)`, and no CSS property accepts
 * "a percentage and nothing else" -- validating against `width` would wave
 * through `auto`, `min-content` and every other keyword `width` allows, all of
 * which break the mix.
 */
const PERCENTAGE = /^-?\d*\.?\d+%$/;

/**
 * Fallback for environments with no CSS parser (jsdom).
 *
 * Deliberately PERMISSIVE. Every browser has `CSS.supports`, so this path is
 * never the real gate -- and a strict grammar here would reject values the
 * browser accepts, which is the failure mode this module exists to avoid. It
 * refuses characters no CSS value contains and otherwise defers.
 *
 * The consequence for tests is worth stating plainly: under jsdom these
 * checks exercise the PLUMBING, not the parser. The tests that matter stub
 * `CSS.supports` and assert which property we hand it.
 */
const CSS_SHAPED = /^[-#\w\s.,()%/+*]+$/;

function hasNativeSupport () {
    return typeof CSS !== 'undefined' && CSS && typeof CSS.supports === 'function';
}

/**
 * Is `value` something the named CSS property will actually accept?
 *
 * Empty is always valid: '' is unset, a first-class value meaning "the player
 * paints its own default". Screening it out would break the clear path.
 *
 * @param {string} property   a real CSS property to validate against, e.g. 'padding'
 * @param {string} value
 * @param {string} [format]   'percentage' for the color-mix percentages
 * @returns {boolean}
 */
export function isValidCssValue (property, value, format) {
    if (value === '' || value === null || value === undefined) return true;
    if (typeof value !== 'string') return false;

    const trimmed = value.trim();
    if (trimmed === '') return true;

    if (format === 'percentage') return PERCENTAGE.test(trimmed);

    if (hasNativeSupport()) {
        // A custom property would accept literally anything, so this
        // deliberately validates against the REAL property the token feeds.
        return CSS.supports(property, trimmed);
    }

    return CSS_SHAPED.test(trimmed);
}

/**
 * The CSS property a token's value is measured against, by input type.
 * Used when a meta entry does not name one of its own.
 */
export const DEFAULT_PROPERTY = {
    color: 'color',
    length: 'width',
    // NO entry for `text`. A text field is prose until a meta entry says
    // otherwise -- a transcript notice and a host URL both use it, and holding
    // those to a CSS grammar would reject every legitimate value. Screening
    // text requires an explicit `cssProperty`.
};

/**
 * Screen a value for a metaMap entry.
 *
 * @param {object} meta   a metaMap entry
 * @param {string} value
 * @returns {boolean}
 */
export function isValidForMeta (meta, value) {
    if (!meta) return true;
    const property = meta.cssProperty || DEFAULT_PROPERTY[meta.inputType];
    // No property to check against means this is prose, not CSS -- a
    // transcript notice or a host URL. Not this module's business.
    if (!property) return true;
    return isValidCssValue(property, value, meta.cssFormat);
}
